(() => {
  'use strict';

  const EVENT_VERSION = 1;
  const QUOTE_PATH = '/pages/sell-to-gpused';
  const ATTRIBUTION_TTL_MS = 30 * 60 * 1000;
  const ATTRIBUTION_KEY = 'gpused-quote-funnel:attribution:v1';
  const PENDING_KEY = 'gpused-quote-funnel:pending:v1';
  const SUCCESS_PREFIX = 'gpused-quote-funnel:success:v1:';
  const queuedEvents = [];
  const startedForms = new Set();
  const reportedErrors = new Set();
  const successfulForms = new Set();
  let flushTimer = null;
  let flushAttempts = 0;

  function storage() {
    try {
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  function readStoredJson(key) {
    const currentStorage = storage();
    if (!currentStorage) return null;

    try {
      const value = JSON.parse(currentStorage.getItem(key));
      if (
        !value ||
        typeof value !== 'object' ||
        !Number.isFinite(value.recordedAt) ||
        Date.now() - value.recordedAt > ATTRIBUTION_TTL_MS
      ) {
        currentStorage.removeItem(key);
        return null;
      }
      return value;
    } catch (error) {
      try {
        currentStorage.removeItem(key);
      } catch (storageError) {
        /* Storage can be unavailable in strict privacy modes. */
      }
      return null;
    }
  }

  function writeStoredJson(key, value) {
    const currentStorage = storage();
    if (!currentStorage) return;

    try {
      currentStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* Measurement must never interfere with browsing or form submission. */
    }
  }

  function normaliseSource(value) {
    const source = String(value == null ? '' : value).trim().toLowerCase();
    return /^[a-z0-9][a-z0-9-]{0,79}$/.test(source) ? source : '';
  }

  function normaliseErrorCode(value) {
    const code = String(value == null ? '' : value).trim().toLowerCase();
    return /^[a-z0-9][a-z0-9-]{0,79}$/.test(code) ? code : 'unknown-error';
  }

  function cleanPath(value) {
    const path = String(value == null ? '' : value).trim();
    if (!path.startsWith('/') || path.startsWith('//')) return '/';
    return path.split('?')[0].split('#')[0].slice(0, 240) || '/';
  }

  function sourceFromLocation() {
    try {
      return normaliseSource(new URLSearchParams(window.location.search).get('quote_source'));
    } catch (error) {
      return '';
    }
  }

  function currentSource(explicitSource) {
    const directSource = normaliseSource(explicitSource) || sourceFromLocation();
    if (directSource) return directSource;

    const attribution = readStoredJson(ATTRIBUTION_KEY);
    return normaliseSource(attribution && attribution.source) || 'direct';
  }

  function analyticsPublish() {
    const analytics = window.Shopify && window.Shopify.analytics;
    return analytics && typeof analytics.publish === 'function'
      ? analytics.publish.bind(analytics)
      : null;
  }

  function scheduleFlush() {
    if (flushTimer || flushAttempts >= 10 || !queuedEvents.length) return;
    flushTimer = window.setTimeout(() => {
      flushTimer = null;
      flushAttempts += 1;
      flush();
    }, 500);
  }

  function flush() {
    const publish = analyticsPublish();
    if (!publish) {
      scheduleFlush();
      return;
    }

    flushAttempts = 0;
    while (queuedEvents.length) {
      const event = queuedEvents.shift();
      try {
        Promise.resolve(publish(event.name, event.payload)).catch(() => {});
      } catch (error) {
        /* Analytics failure must never interrupt the customer journey. */
      }
    }
  }

  function publish(name, payload) {
    queuedEvents.push({
      name,
      payload: Object.freeze({
        event_version: EVENT_VERSION,
        ...payload,
      }),
    });
    flush();
  }

  function formKey(value) {
    return normaliseSource(value) || 'quote-form';
  }

  function trackQuoteClick(details) {
    const source = normaliseSource(details && details.source) || 'unattributed';
    const recordedAt = Date.now();
    writeStoredJson(ATTRIBUTION_KEY, { source, recordedAt });
    publish('gpused:quote_click', {
      source,
      origin_path: cleanPath(details && details.originPath),
      destination_path: QUOTE_PATH,
    });
  }

  function trackFormStart(details) {
    const key = formKey(details && details.formKey);
    if (startedForms.has(key)) return;
    startedForms.add(key);

    publish('gpused:quote_form_started', {
      source: currentSource(details && details.source),
      form_key: key,
      page_path: cleanPath(window.location.pathname),
    });
  }

  function trackFormError(details) {
    const key = formKey(details && details.formKey);
    const code = normaliseErrorCode(details && details.errorCode);
    const fingerprint = key + ':' + code;

    if (reportedErrors.has(fingerprint)) return;
    reportedErrors.add(fingerprint);
    window.setTimeout(() => reportedErrors.delete(fingerprint), 1000);

    publish('gpused:quote_form_error', {
      source: currentSource(details && details.source),
      form_key: key,
      error_code: code,
      page_path: cleanPath(window.location.pathname),
    });
  }

  function createAttemptId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (error) {
      /* Use the non-identifying fallback below. */
    }
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  function beginSubmission(details) {
    const pending = {
      attemptId: createAttemptId(),
      source: currentSource(details && details.source),
      formKey: formKey(details && details.formKey),
      recordedAt: Date.now(),
    };
    writeStoredJson(PENDING_KEY, pending);
    return pending.attemptId;
  }

  function trackFormSuccess(details) {
    const pending = readStoredJson(PENDING_KEY);
    const key = formKey((pending && pending.formKey) || (details && details.formKey));
    if (successfulForms.has(key)) return;
    successfulForms.add(key);
    const attemptId = pending && typeof pending.attemptId === 'string'
      ? pending.attemptId.slice(0, 80)
      : '';
    const successKey = SUCCESS_PREFIX + (attemptId || key + ':' + currentSource(details && details.source));
    const currentStorage = storage();

    try {
      if (currentStorage && currentStorage.getItem(successKey)) return;
      if (currentStorage) currentStorage.setItem(successKey, 'reported');
    } catch (error) {
      /* In-memory DOM guards still prevent normal duplicate initialisation. */
    }

    publish('gpused:quote_form_success', {
      source: currentSource((pending && pending.source) || (details && details.source)),
      form_key: key,
      confirmation: 'shopify-rendered-success',
      page_path: cleanPath(window.location.pathname),
    });

    try {
      if (currentStorage) currentStorage.removeItem(PENDING_KEY);
    } catch (error) {
      /* The confirmed success event has already been queued. */
    }
  }

  function quoteLinkFromEvent(event) {
    if (!event || event.isTrusted === false || !(event.target instanceof Element)) return null;
    const link = event.target.closest('a[href]');
    if (!link) return null;

    try {
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin || url.pathname.replace(/\/+$/, '') !== QUOTE_PATH) {
        return null;
      }
      return url;
    } catch (error) {
      return null;
    }
  }

  document.addEventListener('click', (event) => {
    const url = quoteLinkFromEvent(event);
    if (!url) return;
    trackQuoteClick({
      source: url.searchParams.get('quote_source'),
      originPath: window.location.pathname,
    });
  }, { capture: true, passive: true });

  window.addEventListener('load', flush, { once: true });

  window.GPUsedQuoteFunnel = Object.freeze({
    trackQuoteClick,
    trackFormStart,
    trackFormError,
    beginSubmission,
    trackFormSuccess,
  });
  window.dispatchEvent(new CustomEvent('gpused:quote-funnel-ready'));
})();

/* SG2 source capture. The native contact POST owns submission and success. */
(function () {
  'use strict';
  var KEY = 'gpused:seller-source:v1';
  var POLICY = 'gpused.seller-source.v1';
  var TTL = 30 * 60 * 1000;
  var pending = Object.create(null);
  function token(value) { return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/.test(value) ? value : ''; }
  function permitted() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      return Boolean(privacy && privacy.marketingAllowed() === true && privacy.currentVisitorConsent().marketing === 'yes');
    } catch (error) { return false; }
  }
  function clear() {
    pending = Object.create(null);
    try { window.sessionStorage.removeItem(KEY); } catch (error) { /* Storage is optional. */ }
  }
  function validTouch(touch, now) {
    return touch && token(touch.campaign_id) && token(touch.asset_id) && typeof touch.at === 'string' &&
      /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(touch.at) &&
      Number.isFinite(Date.parse(touch.at)) && Date.parse(touch.at) <= now && now - Date.parse(touch.at) <= TTL;
  }
  function observe() {
    if (!permitted()) { clear(); return null; }
    var now = Date.now(), stored = null, params;
    try {
      stored = JSON.parse(window.sessionStorage.getItem(KEY) || 'null');
      if (!stored || !validTouch(stored.first, now) || !validTouch(stored.last, now) || stored.first.at > stored.last.at) stored = null;
    } catch (error) { stored = null; }
    try { params = new URLSearchParams(window.location.search); } catch (error) { return stored; }
    var campaign = token(params.get('sg_campaign')), asset = token(params.get('sg_asset'));
    if (params.getAll('sg_campaign').length > 1 || params.getAll('sg_asset').length > 1) return null;
    if (campaign && asset) {
      var touch = { campaign_id: campaign, asset_id: asset, at: new Date(now).toISOString() };
      // A page refresh or submit re-read does not fabricate a new touch time.
      if (!stored) stored = { first: touch, last: touch };
      else if (stored.last.campaign_id !== campaign || stored.last.asset_id !== asset) stored.last = touch;
      try { window.sessionStorage.setItem(KEY, JSON.stringify(stored)); } catch (error) { /* Same-page capture still works. */ }
    }
    return stored;
  }
  function identity(prefix) {
    if (!window.crypto || typeof window.crypto.getRandomValues !== 'function') return '';
    var bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return prefix + Array.from(bytes, function (value) { return value.toString(16).padStart(2, '0'); }).join('').toUpperCase();
  }
  function capture(details) {
    try {
      if (!permitted()) { clear(); return ''; }
      var history = observe(), params = new URLSearchParams(window.location.search);
      var key = token(details.formKey), source = token(details.quoteSource);
      if (!key || params.getAll('sg_ref').length > 1) return '';
      var first = history && history.first, last = history && history.last;
      var referral = token(params.get('sg_ref'));
      var claim = JSON.stringify([source, first, last, referral]);
      if (pending[key] && pending[key].claim === claim) return pending[key].wire;
      var attempt = identity('SGA-'), journey = identity('JNY-');
      if (!attempt || !journey) return '';
      var wire = JSON.stringify({ v: 1, policy: POLICY, attempt_id: attempt, journey_id: journey,
        captured_at: new Date().toISOString(), marketing_allowed: true, permission_source: 'shopify_customer_privacy_api',
        quote_source: source, first_campaign_id: first ? first.campaign_id : '', first_asset_id: first ? first.asset_id : '', first_at: first ? first.at : '',
        last_campaign_id: last ? last.campaign_id : '', last_asset_id: last ? last.asset_id : '', last_at: last ? last.at : '', referral_code: referral });
      pending[key] = { claim: claim, wire: wire };
      return wire;
    } catch (error) { return ''; }
  }
  document.addEventListener('visitorConsentCollected', function () { clear(); observe(); });
  window.GPUsedSellerGrowth = Object.freeze({ capture: capture });
  observe();
}());
